<script setup lang="ts">
// The console's navigation, in Arco Design Pro's light menu: icons on the
// groups, children indented under the group's text. App.vue renders it in
// the side bar, or, on a narrow screen, in a drawer opened from the navbar
// — the way Arco Pro does it below its lg breakpoint.
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import MenuIcon from '@/components/icons/MenuIcon.vue'
import { financeSummary, watchFinance } from '@/store/finance'
import { ticketSummary, watchTickets } from '@/store/tickets'
import { updateAvailable, watchUpdate } from '@/store/sysupdate'

const props = defineProps<{
  collapsed: boolean
  /** The 收起 row at the foot of the menu. Not in the drawer. */
  collapsible: boolean
}>()
const emit = defineEmits<{ collapse: [boolean] }>()

// 展开菜单's tooltip, closed by the click that expands the menu.
const foldTip = ref(false)
function toggleFold() {
  foldTip.value = false
  emit('collapse', !props.collapsed)
}

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const go = (name: string) => router.push({ name })

// A page under a menu entry (a node, a site, a line group's records)
// keeps that entry lit, so the menu still says where you are.
const PARENT: Record<string, string> = {
  'node-detail': 'nodes',
  'domain-detail': 'domains',
  rules: 'domains',
  'line-group-dns': 'line-groups',
  'ticket-detail': 'tickets',
}
const selected = () => PARENT[route.name as string] ?? (route.name as string)

// 财务管理's review pages say how much waits on them: a document to look
// at, or a transfer or refund Alipay refused that needs retrying.
watchFinance()
const waiting = computed(() => {
  const s = financeSummary.value
  return {
    remittances: s?.pending_remittances ?? 0,
    withdrawals: (s?.pending_withdrawals ?? 0) + (s?.failed_withdrawals ?? 0),
    invoices: s?.pending_invoices ?? 0,
    refunds: (s?.pending_refunds ?? 0) + (s?.failed_refunds ?? 0),
  }
})
// 工单列表 says how many tickets wait on support: new, or the customer
// wrote last.
watchTickets()
const openTickets = computed(() => ticketSummary.value?.open ?? 0)
// 投诉管理 says how many complaints wait on support: nobody took them on
// yet, or nobody answered them.
const openComplaints = computed(() => ticketSummary.value?.complaints_open ?? 0)
// 系统更新 shows a dot while a newer control plane is out.
watchUpdate()
</script>

<template>
  <div class="side">
  <a-menu
    class="side-menu"
    :selected-keys="[selected()]"
    :collapsed="collapsed"
    :show-collapse-button="false"
    auto-open-selected
    :level-indent="34"
  >
    <a-menu-item key="dashboard" @click="go('dashboard')">
      <template #icon><MenuIcon name="dashboard" /></template>
      {{ t('nav.dashboard') }}
    </a-menu-item>
    <a-sub-menu key="grp-nodes">
      <template #icon><MenuIcon name="nodes" /></template>
      <template #title>{{ t('menu.grpNodes') }}</template>
      <a-menu-item key="nodes" @click="go('nodes')">{{ t('nav.nodes') }}</a-menu-item>
      <a-menu-item key="regions" @click="go('regions')">{{ t('nav.regions') }}</a-menu-item>
      <a-menu-item key="tasks" @click="go('tasks')">{{ t('nav.tasks') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-sites">
      <template #icon><MenuIcon name="sites" /></template>
      <template #title>{{ t('menu.grpSites') }}</template>
      <a-menu-item key="domains" @click="go('domains')">{{ t('nav.domains') }}</a-menu-item>
      <a-menu-item key="certificates" @click="go('certificates')">{{ t('certificates.title') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-l4">
      <template #icon><MenuIcon name="l4" /></template>
      <template #title>{{ t('menu.grpL4') }}</template>
      <a-menu-item key="l4-rules" @click="go('l4-rules')">{{ t('l4.title') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-dns">
      <template #icon><MenuIcon name="dns" /></template>
      <template #title>{{ t('menu.grpDns') }}</template>
      <a-menu-item key="line-groups" @click="go('line-groups')">{{ t('nav.lineGroups') }}</a-menu-item>
      <a-menu-item key="cname-zones" @click="go('cname-zones')">{{ t('nav.cnameZones') }}</a-menu-item>
      <a-menu-item key="dns-providers" @click="go('dns-providers')">{{ t('nav.dnsProviders') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-security">
      <template #icon><MenuIcon name="security" /></template>
      <template #title>{{ t('menu.grpSecurity') }}</template>
      <a-menu-item key="ip-lists" @click="go('ip-lists')">{{ t('ipLists.title') }}</a-menu-item>
      <a-menu-item key="firewall" @click="go('firewall')">{{ t('firewall.title') }}</a-menu-item>
      <a-menu-item key="ban-logs" @click="go('ban-logs')">{{ t('banLogs.title') }}</a-menu-item>
      <a-menu-item key="security-events" @click="go('security-events')">{{ t('nav.securityEvents') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-observe">
      <template #icon><MenuIcon name="analytics" /></template>
      <template #title>{{ t('menu.grpObserve') }}</template>
      <a-menu-item key="analytics" @click="go('analytics')">{{ t('nav.analytics') }}</a-menu-item>
      <a-menu-item key="access-stats" @click="go('access-stats')">{{ t('nav.accessStats') }}</a-menu-item>
      <a-menu-item key="origin-stats" @click="go('origin-stats')">{{ t('nav.originStats') }}</a-menu-item>
      <a-menu-item key="access-logs" @click="go('access-logs')">{{ t('nav.accessLogs') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-customers">
      <template #icon><MenuIcon name="customers" /></template>
      <template #title>{{ t('menu.grpCustomers') }}</template>
      <a-menu-item key="users" @click="go('users')">{{ t('nav.users') }}</a-menu-item>
      <a-menu-item key="user-groups" @click="go('user-groups')">{{ t('nav.userGroups') }}</a-menu-item>
      <a-menu-item key="realname-settings" @click="go('realname-settings')">{{ t('realnameSettings.title') }}</a-menu-item>
      <a-menu-item key="member-settings" @click="go('member-settings')">{{ t('memberSettings.title') }}</a-menu-item>
      <a-menu-item key="packages" @click="go('packages')">{{ t('packages.title') }}</a-menu-item>
      <a-menu-item key="subscriptions" @click="go('subscriptions')">{{ t('subscriptions.title') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-finance">
      <template #icon><MenuIcon name="finance" /></template>
      <template #title>{{ t('menu.grpFinance') }}</template>
      <a-menu-item key="topups" @click="go('topups')">{{ t('topups.title') }}</a-menu-item>
      <a-menu-item key="finance-orders" @click="go('finance-orders')">{{ t('finOrders.title') }}</a-menu-item>
      <a-menu-item key="finance-remittances" @click="go('finance-remittances')">
        <span class="with-count">{{ t('remits.title') }}<a-badge v-if="waiting.remittances" :count="waiting.remittances" class="count" /></span>
      </a-menu-item>
      <a-menu-item key="finance-withdrawals" @click="go('finance-withdrawals')">
        <span class="with-count">{{ t('withdrawals.title') }}<a-badge v-if="waiting.withdrawals" :count="waiting.withdrawals" class="count" /></span>
      </a-menu-item>
      <a-menu-item key="finance-invoices" @click="go('finance-invoices')">
        <span class="with-count">{{ t('invoices.title') }}<a-badge v-if="waiting.invoices" :count="waiting.invoices" class="count" /></span>
      </a-menu-item>
      <a-menu-item key="finance-refunds" @click="go('finance-refunds')">
        <span class="with-count">{{ t('refunds.title') }}<a-badge v-if="waiting.refunds" :count="waiting.refunds" class="count" /></span>
      </a-menu-item>
      <a-menu-item key="payment" @click="go('payment')">{{ t('payment.title') }}</a-menu-item>
      <a-menu-item key="finance-settings" @click="go('finance-settings')">{{ t('finSettings.title') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-tickets">
      <template #icon><MenuIcon name="tickets" /></template>
      <template #title>{{ t('menu.grpTickets') }}</template>
      <a-menu-item key="tickets" @click="go('tickets')">
        <span class="with-count">{{ t('tickets.title') }}<a-badge v-if="openTickets" :count="openTickets" class="count" /></span>
      </a-menu-item>
      <a-menu-item key="complaints" @click="go('complaints')">
        <span class="with-count">{{ t('complaints.title') }}<a-badge v-if="openComplaints" :count="openComplaints" class="count" /></span>
      </a-menu-item>
      <a-menu-item key="ticket-templates" @click="go('ticket-templates')">{{ t('ticketTemplates.title') }}</a-menu-item>
      <a-menu-item key="ticket-settings" @click="go('ticket-settings')">{{ t('ticketSettings.title') }}</a-menu-item>
    </a-sub-menu>
    <!-- 消息通知: how the platform reaches people, and what it says. -->
    <a-sub-menu key="grp-messages">
      <template #icon><MenuIcon name="messages" /></template>
      <template #title>{{ t('menu.grpMessages') }}</template>
      <a-menu-item key="mail-settings" @click="go('mail-settings')">{{ t('mail.title') }}</a-menu-item>
      <a-menu-item key="sms-settings" @click="go('sms-settings')">{{ t('smsSettings.title') }}</a-menu-item>
      <a-menu-item key="message-templates" @click="go('message-templates')">{{ t('messageTemplates.title') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-system">
      <template #icon><MenuIcon name="system" /></template>
      <template #title>{{ t('menu.grpSystem') }}</template>
      <a-menu-item key="system-settings" @click="go('system-settings')">{{ t('systemSettings.title') }}</a-menu-item>
      <a-menu-item key="captcha-settings" @click="go('captcha-settings')">{{ t('captchaSettings.title') }}</a-menu-item>
      <a-menu-item key="system-update" @click="go('system-update')">
        <span class="with-count">{{ t('systemUpdate.title') }}<a-badge v-if="updateAvailable" :count="1" dot class="count" /></span>
      </a-menu-item>
      <a-menu-item key="default-pages" @click="go('default-pages')">{{ t('defaultPages.title') }}</a-menu-item>
      <a-menu-item key="console-domains" @click="go('console-domains')">{{ t('consoleDomains.title') }}</a-menu-item>
      <a-menu-item key="assistant-settings" @click="go('assistant-settings')">{{ t('assistantSettings.title') }}</a-menu-item>
      <a-menu-item key="control-tls" @click="go('control-tls')">{{ t('controlTLS.title') }}</a-menu-item>
      <a-menu-item key="clickhouse" @click="go('clickhouse')">{{ t('clickhouse.title') }}</a-menu-item>
      <a-menu-item key="audit" @click="go('audit')">{{ t('audit.title') }}</a-menu-item>
      <a-menu-item key="license" @click="go('license')">{{ t('license.title') }}</a-menu-item>
    </a-sub-menu>
  </a-menu>
  <!-- 收起, Tencent Cloud's: the menu's own foot, under a rule — its icon
       and word where the menu's icons and titles are; collapsed, the icon
       alone, centred under the icons, its arrow turned round. -->
  <!-- 展开菜单 on hover while collapsed; held in hand, so the click that
       expands the menu takes it away (a disabled tooltip left open stayed
       on screen until the next click elsewhere). -->
  <a-tooltip
    v-if="collapsible"
    :content="t('app.expandMenu')"
    position="right"
    :popup-visible="foldTip && collapsed"
    @popup-visible-change="(v: boolean) => (foldTip = v)"
  >
    <button
      type="button"
      class="fold"
      :class="{ collapsed }"
      :aria-label="collapsed ? t('app.expandMenu') : t('app.collapseMenu')"
      @click="toggleFold"
    >
      <svg class="fold-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true">
        <rect x="3.4" y="4.4" width="17.2" height="15.2" rx="1.6" />
        <path d="M9.2 4.4v15.2" />
        <path :d="collapsed ? 'M13.3 9.4 15.9 12l-2.6 2.6' : 'M16.1 9.4 13.5 12l2.6 2.6'" stroke-linecap="round" />
      </svg>
      <span class="fold-text">{{ t('app.foldMenu') }}</span>
    </button>
  </a-tooltip>
  </div>
</template>

<style scoped>
/* The column: the menu, scrolling on its own, over the 收起 row at its
   foot, which stays put. */
.side {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.side-menu {
  flex: 1;
  min-height: 0;
}
/* The 收起 row: the menu's item rhythm — its icon at the icons' 20px, its
   word at the titles' 54px — under the navbar's rule. At 48px the icon
   alone, at the collapsed icons' 15px, the word narrowing away with the
   side bar's own transition. */
.fold {
  display: flex;
  flex: none;
  align-items: center;
  gap: 16px;
  width: 100%;
  height: 48px;
  padding: 0 0 0 20px;
  overflow: hidden;
  border: none;
  border-top: 1px solid var(--color-border);
  background: none;
  color: var(--color-text-2);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
  transition:
    padding 0.2s cubic-bezier(0.34, 0.69, 0.1, 1),
    gap 0.2s cubic-bezier(0.34, 0.69, 0.1, 1),
    color 0.15s,
    background-color 0.15s;
}
.fold:hover {
  color: var(--color-text-1);
  background-color: var(--color-fill-2);
}
.fold.collapsed {
  gap: 0;
  padding-left: 15px;
}
.fold-icon {
  flex: none;
  width: 18px;
  height: 18px;
}
.fold-text {
  max-width: 160px;
  overflow: hidden;
  white-space: nowrap;
  transition:
    max-width 0.2s cubic-bezier(0.34, 0.69, 0.1, 1),
    opacity 0.2s cubic-bezier(0.34, 0.69, 0.1, 1);
}
.fold.collapsed .fold-text {
  max-width: 0;
  opacity: 0;
}
/* A group's pages, as Tencent Cloud draws them: a hairline down from the
   group's icon, centred on it — the list sits 8px in, the icon's centre
   is 21px further (12px of padding, half of 18px), measured at 29px — and
   on it a short bar for the page under the pointer and the open one.
   Both above the items' grounds, the bar over the line. */
:deep(.arco-menu-inline-content) {
  position: relative;
}
:deep(.arco-menu-inline-content)::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
  left: 20.5px;
  width: 1px;
  background: var(--color-border-2);
}
/* The bar follows the pointer, as Tencent Cloud's does: every page has
   one, folded flat; the one under the pointer unfolds in grey with Arco's
   hover ground, the open one stays unfolded in its title's blue. */
:deep(.arco-menu-inline-content .arco-menu-item)::after {
  content: '';
  position: absolute;
  z-index: 2;
  top: 10px;
  bottom: 10px;
  left: 20px;
  width: 2px;
  background: var(--color-text-3);
  transform: scaleY(0);
  transition:
    transform 0.2s cubic-bezier(0.34, 0.69, 0.1, 1),
    background-color 0.2s;
}
:deep(.arco-menu-inline-content .arco-menu-item:hover)::after {
  transform: scaleY(1);
}
:deep(.arco-menu-inline-content .arco-menu-selected)::after {
  transform: scaleY(1);
  background: rgb(var(--primary-6));
}
/* Arco Pro's menu (src/components/menu): 18px icons — Arco's own default
   is the 14px of the text beside them — with the group headers' icon and
   title centred on one line. The expand arrow keeps its size. */
:deep(.arco-menu-inner) .arco-menu-inline-header {
  display: flex;
  align-items: center;
}
:deep(.arco-menu-inner) .arco-icon:not(.arco-icon-down) {
  font-size: 18px;
}
/* Arco Pro's menu scrollbar (its default layout's .menu-wrapper): a thin
   rounded thumb in the text's lightest grey, inset in a 12px track, there
   whenever the menu is taller than the screen. */
:deep(.arco-menu-inner)::-webkit-scrollbar {
  width: 12px;
  height: 4px;
}
:deep(.arco-menu-inner)::-webkit-scrollbar-thumb {
  border: 4px solid transparent;
  border-radius: 7px;
  background-clip: padding-box;
  background-color: var(--color-text-4);
}
:deep(.arco-menu-inner)::-webkit-scrollbar-thumb:hover {
  background-color: var(--color-text-3);
}
/* The open group's icon in the primary blue, its facet a lighter blue —
   as Arco tints the title beside it (see MenuIcon for the greys). */
:deep(.arco-menu-selected) {
  --mi-main: rgb(var(--primary-6));
  --mi-sub: rgb(var(--primary-3));
}
/* Collapsed to 48px, the icons are centred: Arco's 12px item padding
   centres its own 16px icons, so these sat a pixel right of the middle,
   and of the logo above them in the navbar. */
.arco-menu-collapsed :deep(.arco-menu-item),
.arco-menu-collapsed :deep(.arco-menu-pop-header) {
  padding-left: 11px;
}
/* A count of what waits, at the item's right edge — out of the line, which
   is the indent and the text inline, so it never pushes the text down. */
:deep(.arco-menu-item) {
  position: relative;
}
.count {
  position: absolute;
  top: 50%;
  right: 16px;
  line-height: 1;
  transform: translateY(-50%);
}
.count :deep(.arco-badge-number) {
  box-shadow: none;
}
</style>
